function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = () => {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = (event) => {
    if (event.code?.toUpperCase() !== 'TAB') return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first?.focus();
    }
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last?.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);
  elementToFocus?.focus();
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);
  elementToFocus?.focus();
}

function onKeyUpEscape(event) {
  if (event.code?.toUpperCase() !== 'ESCAPE') return;
  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;
  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement?.setAttribute('aria-expanded', false);
  summaryElement?.focus();
}

function debounce(fn, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: `application/${type}`,
    },
  };
}

if (typeof window.CartPerformance === 'undefined') {
  window.CartPerformance = {
    createStartingMarker() {
      return typeof performance !== 'undefined' ? performance.now() : Date.now();
    },
    measure(_name, callback) {
      return typeof callback === 'function' ? callback() : undefined;
    },
    measureFromMarker() {},
    measureFromEvent() {},
  };
}
var CartPerformance = window.CartPerformance;

if (!customElements.get('quantity-input')) {
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input');
      this.changeEvent = new Event('change', { bubbles: true });
      if (!this.input) return;
      this.input.addEventListener('change', this.validateQtyRules.bind(this));
      this.querySelectorAll('button').forEach((button) =>
        button.addEventListener('click', this.onButtonClick.bind(this))
      );
    }

    connectedCallback() {
      this.validateQtyRules();
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.validateQtyRules());
      }
    }

    disconnectedCallback() {
      this.quantityUpdateUnsubscriber?.();
    }

    onButtonClick(event) {
      event.preventDefault();
      if (!this.input) return;
      const previousValue = this.input.value;
      event.currentTarget.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
      if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
    }

    validateQtyRules() {
      if (!this.input) return;
      const value = parseInt(this.input.value, 10) || 0;
      const min = this.input.min ? parseInt(this.input.min, 10) : null;
      const max = this.input.max ? parseInt(this.input.max, 10) : null;
      const minus = this.querySelector('button[name="minus"]');
      const plus = this.querySelector('button[name="plus"]');
      if (minus) minus.classList.toggle('disabled', min !== null && value <= min);
      if (plus) plus.classList.toggle('disabled', max !== null && value >= max);
    }
  }

  customElements.define('quantity-input', QuantityInput);
  window.QuantityInput = QuantityInput;
}

// Al Imran: keep product quantity controls set-wise without replacing Shopify cart state.
(function () {
  const QuantityInputClass = window.QuantityInput || customElements.get('quantity-input');
  if (!QuantityInputClass || QuantityInputClass.prototype.__alImranSetWisePatched) return;
  QuantityInputClass.prototype.__alImranSetWisePatched = true;
  const original = QuantityInputClass.prototype.onButtonClick;

  QuantityInputClass.prototype.onButtonClick = function (event) {
    const input = this.input;
    if (!input) return original.call(this, event);

    const declaredSetQty = parseInt(input.dataset.setQty, 10);
    const dataMin = parseInt(input.dataset.min, 10);
    const inputStep = parseInt(input.step, 10);
    const step = Math.max(1, declaredSetQty || dataMin || inputStep || 1);
    const isSetWise = input.dataset.setWise === 'true' || step > 1;
    if (!isSetWise) return original.call(this, event);

    event.preventDefault();
    const current = Math.max(0, parseInt(input.value, 10) || 0);
    const next = event.currentTarget.name === 'plus'
      ? (current === 0 ? step : current + step)
      : Math.max(0, current - step);
    const max = input.max ? parseInt(input.max, 10) : null;
    const finalValue = max !== null ? Math.min(next, max) : next;

    if (finalValue !== current) {
      input.value = finalValue;
      input.setAttribute('value', finalValue);
      input.dispatchEvent(this.changeEvent);
    }
  };
})();

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}
